import { CommonModule } from '@angular/common';
import { Component, forwardRef, input, Input, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
	ControlContainer,
	ControlValueAccessor,
	FormGroupDirective,
	FormsModule,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	UntypedFormControl,
	UntypedFormGroup,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PortalCoreFeature } from '@ats/cm/core';
import { AtsIconsModule } from '@ats/icons';
import { LocalizationService } from '@cm/core/localization';
import { SettingsService } from '@cm/core/settings';
import { CmMiniButtonDirective } from '@cm/shared/ui/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable, of } from 'rxjs';
import {
	combineLatestWith,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';
import { CMOption } from '../../models';

// https://stackoverflow.com/questions/45659742/angular4-no-value-accessor-for-form-control/45659791

type ComparitorCallback = (option: CMOption, value: any) => any;

@UntilDestroy()
@Component({
	selector: 'cm-autocomplete',
	imports: [
		CommonModule,
		CmMiniButtonDirective,
		MatAutocompleteModule,
		MatFormFieldModule,
		MatInputModule,
		MatTooltipModule,
		MatButtonModule,
		FormsModule,
		ReactiveFormsModule,
		AtsIconsModule,
	],
	templateUrl: './autocomplete.component.html',
	styleUrls: ['./autocomplete.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CmAutocompleteComponent),
			multi: true,
		},
	],
})
export class CmAutocompleteComponent implements ControlValueAccessor, OnInit {
	@Input() options$!: Observable<CMOption[]>;
	@Input() formControlName!: string;
	@Input() _value: CMOption | null = null;
	@Input() valueComparitor: ComparitorCallback | null = null;
	@Input() matchRequired = true;
	@Input() appearance: MatFormFieldAppearance = 'fill';
	@Input() maxlength: string | null = null;
	@Input() hideLabel: boolean = false;
	@Input() filterExact: boolean = true;
	readonly stickyOptions = input<CMOption[]>([]);
	readonly stickyOptions$ = toObservable(this.stickyOptions);

	filteredOptions$!: Observable<CMOption[]>;
	clearLoc$!: Observable<string>;
	optionsSnapshot: CMOption[] = [];
	formGroup!: UntypedFormGroup;
	onChange = (_: any) => {};
	onTouched = () => {
		this.control.markAsTouched();
	};
	readonly locFeature: PortalCoreFeature;

	get value(): CMOption | null {
		return this._value;
	}

	set value(val: CMOption | null) {
		if (!!val) {
			this._value = val.value;
			this.onChange(val.value);
			this.onTouched();
		}
	}

	get control(): UntypedFormControl {
		return this.formGroup.get(this.formControlName) as UntypedFormControl;
	}

	get required(): boolean {
		if (!this.formGroup || !this.control) {
			return false;
		}
		const { validator } = this.control;
		if (validator) {
			const validation = validator(new UntypedFormControl());
			return validation !== null && validation['required'] === true;
		}
		return false;
	}

	get disabled(): boolean {
		if (!this.formGroup || !this.control) {
			return false;
		}
		return this.control.disabled;
	}

	constructor(
		private readonly parentForm: FormGroupDirective,
		private readonly controlContainer: ControlContainer,
		private readonly settings: SettingsService,
		private readonly locService: LocalizationService
	) {
		this.locFeature = this.settings.coreFeature;
		this.clearLoc$ = this.locService
			.find$(`${this.locFeature}/System.Title.Clear`)
			.pipe(map(loc => loc?.value || ''));
	}

	ngOnInit(): void {
		this.formGroup = this.controlContainer.control as UntypedFormGroup;

		this.options$
			.pipe(
				filter(options => !!options),
				untilDestroyed(this)
			)
			.subscribe(options => (this.optionsSnapshot = options));

		this.filteredOptions$ = this.options$.pipe(
			filter(options => !!options),
			switchMap(options =>
				(this.control?.valueChanges || of(null)).pipe(
					distinctUntilChanged(),
					startWith(this.control?.value),
					map(value => {
						if (!value) {
							return null;
						}
						const foundValue = this.displayFn(value);
						return !foundValue || foundValue === value.toString()
							? value.toString()
							: foundValue;
					}),
					tap(option => {
						if (option && this.matchRequired) {
							const matchedOptions = options.filter(opt => {
								return (
									(opt.display.toString() || '').toLowerCase() ===
									option.toString().toLowerCase()
								);
							});
							if (matchedOptions.length === 0) {
								this.control?.setErrors({ noMatch: true });
							}
						}
					}),
					combineLatestWith(this.stickyOptions$),
					map(([name, stickyOptions]) => {
						return name
							? this.filterOptions(name, options)
							: [...stickyOptions, ...options.slice()];
					})
				)
			)
		);

		const valueChanges$ = this.control.valueChanges.pipe(distinctUntilChanged());

		combineLatest([valueChanges$, this.options$])
			.pipe(
				untilDestroyed(this),
				filter(([value]) => !!value)
			)
			.subscribe(([value, options]) => {
				if (
					!options
						.map(opt => (opt.value || '').toString().toLowerCase())
						.includes(value?.toString()?.toLowerCase())
				) {
					const option = options.find(
						opt =>
							(opt.display || '').toLowerCase() === value?.toString()?.toLowerCase()
					);
					if (option) {
						this.control.setValue(option);
					}
				}
			});
	}

	clearValue(e: Event, trigger: MatAutocompleteTrigger, inp: HTMLInputElement): void {
		e.stopPropagation();
		this.control.setValue(null);
		this.control.markAsDirty();
		trigger.openPanel();
		inp.focus();
	}

	displayFn = (value: any): string => {
		if (!value || !this.optionsSnapshot) {
			return '';
		}
		if (typeof value !== 'string' && value.display) {
			value = value.display;
		}
		const optionsToSearch = [...this.optionsSnapshot, ...(this.stickyOptions() ?? [])];
		const option = !!this.valueComparitor
			? optionsToSearch.find(opt =>
					!!this.valueComparitor ? this.valueComparitor(opt, value) : null
			  )
			: optionsToSearch.find(opt => opt.value === value);
		return option && option.display ? option.display : value;
	};

	writeValue(value: any): void {
		if (value !== undefined) {
			this.value = value;
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	handleSubmit(): void {
		this.parentForm.ngSubmit.emit();
	}

	private filterOptions(name: string, options: CMOption[]): CMOption[] {
		const filterValue = name.toString().trim().toLowerCase();

		const exactMatches = options.filter(
			opt => (opt.display.toString() || '').trim().toLowerCase() === filterValue
		);

		if (this.filterExact && exactMatches.length > 0) {
			return this.appendStickyOptions(exactMatches);
		}

		return this.appendStickyOptions(
			options.filter(opt =>
				(opt.display.toString() || '').toLowerCase().includes(filterValue)
			)
		);
	}

	private appendStickyOptions(options: CMOption[]): CMOption[] {
		if (this.stickyOptions().length > 0) {
			return [
				...this.stickyOptions(),
				...options.filter(opt => !this.stickyOptions().includes(opt)),
			];
		}
		return options;
	}
}
